/**
 * F-27 → F-50 bridge: binds the toolbar's ADD-COMMENT HOOK to the comments
 * feature, per the registry contract — `bindAddComment` is called ONCE at
 * feature-module scope (this module is imported by the comments components,
 * which BoardCanvas imports before the first toolbar render, so the toolbar
 * button flips from disabled to live). Re-import under HMR re-binds (the
 * hook replaces the previous handler).
 *
 * The handler forwards to whichever board view is currently ACTIVE: the
 * mounted CommentOverlays registers its (session, ui-store) pair via
 * `activateCommentsUi`. The hook fires from a toolbar that only exists
 * inside an active board view, so the pair is present whenever the button
 * is clickable; the session identity check guards the teardown races
 * (board switch mid-click).
 */
import type { BoardSession } from "../../core/session.js";
import { bindAddComment } from "../../registries/toolbar-actions/index.js";
import { anchorForTarget, targetPinPosition } from "./anchoring.js";
import type { CommentsUiStore } from "./store.js";

interface ActiveCommentsUi {
  session: BoardSession;
  ui: CommentsUiStore;
}

let active: ActiveCommentsUi | null = null;

/**
 * Register the mounted board view's comments UI as the add-comment sink.
 * Returns the deregistration function (mount-scoped, like an effect cleanup).
 */
export function activateCommentsUi(session: BoardSession, ui: CommentsUiStore): () => void {
  const entry: ActiveCommentsUi = { session, ui };
  active = entry;
  return () => {
    if (active === entry) active = null;
  };
}

bindAddComment((session, target) => {
  if (active === null || active.session !== session) return;
  // Open the composer anchored at the target's rect: for a cell RANGE the
  // toolbar already passed the selection's ANCHOR cell (registry contract),
  // so the pin lands on that cell's center; elements pin to their center.
  active.ui.getState().openDraft({
    anchor: anchorForTarget(target),
    position: targetPinPosition(target),
  });
});
